import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GoogleMap as Map, LoadScript, Marker } from '@react-google-maps/api';
import { generateUniqueId } from '../../utils';

const containerStyle = {
    width: '100%',
    height: '450px',
};

const defaultCoords = {
    lat: 40.7128,
    lng: -74.006,
};

const DEFAULT_ZOOM = 12;
const GOOGLE_MAPS_API_KEY = 'AIzaSyBYACuAZLAad-uadZ8NViL8tQCiiy-JeQM';

interface MapMarker {
    id: string;
    lat: number;
    lng: number;
    zipCode: string;
}

interface MapProps {
    onPointSelect: (items: MapMarker[]) => void;
    pointsMaxCount?: number;
    preselectedPoints?: MapMarker[];
    isZip?: boolean;
}

const libraries: ('places' | 'geometry' | 'drawing' | 'visualization')[] = [ 'places' ];

const GoogleMap = ({
    onPointSelect,
    pointsMaxCount = 1,
    preselectedPoints = [],
    isZip = false,
}: MapProps) => {
    const [ markers, setMarkers ] = useState<MapMarker[]>([]);
    const [ center, setCenter ] = useState(defaultCoords);
    const [ isMapReady, setIsMapReady ] = useState(false);
    const mapRef = useRef<google.maps.Map | null>(null);

    useEffect(() => {
        (
            async () => {
                if (preselectedPoints && !markers.length && isMapReady) {
                    if (isZip) {
                        const points: MapMarker[] = [];
                        for (const item of preselectedPoints) {
                            const coordinates = await getPointFromZipCode(item.zipCode);
                            if (!coordinates) {
                                continue;
                            }

                            points.push({
                                id: generateUniqueId(),
                                zipCode: item.zipCode,
                                ...coordinates,
                            });
                        }
                        setMarkers(points);
                        for (const point of points) {
                            await handleNewPointClick(point);
                        }
                        return;
                    }

                    for (const point of preselectedPoints) {
                        await handleNewPointClick(point);
                        setCenter(point);
                    }
                }
            }
        )();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ isMapReady ]);

    useEffect(() => {
        onPointSelect(markers);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ markers ]);

    const onLoad = useCallback((map: google.maps.Map) => {
        mapRef.current = map;
        const bounds = new window.google.maps.LatLngBounds(center);
        map.fitBounds(bounds);
        setIsMapReady(true);
    }, [ center ]);

    const fitBounds = useCallback((markers: {
        lat: number;
        lng: number;
    }[]) => {
        if (mapRef.current && markers.length > 1) {
            const bounds = new google.maps.LatLngBounds();
            markers.forEach((marker) => bounds.extend(new google.maps.LatLng(marker.lat, marker.lng)));
            mapRef.current.fitBounds(bounds);
        }
    }, []);

    const getZipCodeFromPoint = async (latLng: {
        lat: number;
        lng: number;
    }): Promise<string> => {
        const geocoder = new google.maps.Geocoder();

        const response = await geocoder.geocode({ location: latLng });

        if (!response || !response.results.length) {
            return '';
        }

        const addressComponents = response.results[0]?.address_components;
        const zipComponent = addressComponents?.find(comp => comp.types.includes('postal_code'));
        if (!zipComponent) {
            return '';
        }

        const zipCode = zipComponent.long_name || zipComponent.short_name;

        return zipCode || '';
    };

    const getPointFromZipCode = async (zipCode: string): Promise<{
                                                                     lat: number;
                                                                     lng: number;
                                                                 } | null> => {
        if (!zipCode) {
            return null;
        }

        const geocoder = new google.maps.Geocoder();

        const response = await geocoder.geocode({ address: zipCode });
        if (!response || !response.results.length) {
            return null;
        }

        const {
            lat,
            lng,
        } = response.results[0].geometry.location;

        return {
            lat: lat(),
            lng: lng(),
        };

    };

    const onMapClick = async (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            // if (markers.length >= pointsMaxCount) {
            //     return toast.warning(`Max count of points (${pointsMaxCount}) has reached`);
            // }
            const point = {
                lat: event.latLng.lat(),
                lng: event.latLng.lng(),
            };
            await handleNewPointClick(point);
        }
    };

    const handleNewPointClick = async (point: {
        lat: number;
        lng: number;
    }) => {
        const newMarker = {
            id: generateUniqueId(),
            lat: point.lat,
            lng: point.lng,
            zipCode: await getZipCodeFromPoint({
                lat: point.lat,
                lng: point.lng,
            }),
        };

        setMarkers((current) => {
            const newMarkers = [ newMarker, ...current ].slice(0, pointsMaxCount);
            fitBounds(newMarkers);
            return newMarkers;
        });
    };

    // const requestCurrentLocation = () => {
    //     if (navigator.geolocation) {
    //         navigator.geolocation.getCurrentPosition(
    //             function (position) {
    //                 const lat = position.coords.latitude;
    //                 const lng = position.coords.longitude;
    //                 setCenter({lat, lng});
    //             },
    //             function (error) {
    //                 console.error("Error Code = " + error.code + " - " + error.message);
    //             }
    //         );
    //     } else {
    //         console.error("Geolocation is not supported by this browser.");
    //     }
    // };
    //
    // useEffect(() => {
    //     requestCurrentLocation();
    // }, []);

    useEffect(() => {
        const currentZoom = mapRef.current?.getZoom();
        if (!currentZoom || currentZoom > DEFAULT_ZOOM) {
            mapRef.current?.setZoom(DEFAULT_ZOOM);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ mapRef.current ]);

    const removeMarker = (marker: MapMarker) => {
        setMarkers(markers =>
            markers.filter(({
                    lat,
                    lng,
                }) =>
                    marker.lat !== lat && marker.lng !== lng,
            ),
        );
    };

    const onMarkerDragEnd = (event: google.maps.MapMouseEvent, id: string) => {
        if (!event.latLng) {
            return;
        }
        setMarkers((prev) => {
            return prev.map((marker) => {
                if (marker.id === id) {
                    return {
                        ...marker,
                        lat: event.latLng!.lat(),
                        lng: event.latLng!.lng(),
                    };
                }

                return marker;
            });
        });
    };

    return (
        <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
            <Map
                mapContainerStyle={containerStyle}
                center={center}
                onLoad={onLoad}
                onClick={onMapClick}
            >
                {markers.map((marker, index) => (
                    <Marker
                        key={index}
                        position={marker}
                        draggable
                        onDragEnd={(event) => onMarkerDragEnd(event, marker.id)}
                        onDblClick={() => removeMarker(marker)}
                    />
                ))}
            </Map>
        </LoadScript>
    );
};

export default GoogleMap;
